import React, { useContext, useState } from 'react';
import { AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import { H, ScrollableLayout } from '../Layout';
import AttachmentsPreview from '../attachments/AttachmentsPreview';
import AttachmentsView from '../attachments/AttachmentsView';
import { createAttachmentInput } from '../attachments/attachmentUtils';
import { SECURITY_ACTIONS, useSecurity, type FeatureName } from '../auth/Security';
import { UserContext } from '../auth/UserContext';
import Loading from '../components/Loading';
import SidePanel from '../components/layout/SidePanel';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import associateTag from '../utils/associateTag';
import { failedToastMessage } from '../utils/commonUtils';
import { useStatusReports } from './useStatusReports';

type StatusReportsPanelProps = {
    onClose: () => void;
    contextTerm?: string;
    contextEntities?: Array<{ type?: string; id?: string }>;
    featureName: FeatureName;
};

export default function StatusReportsPanel({
    onClose,
    contextTerm,
    contextEntities,
    featureName
}: StatusReportsPanelProps) {
    const { attachments, loading, refetch } = useStatusReports({
        contextTerm,
        contextEntities
    });
    const { allowed: canManage } = useSecurity(SECURITY_ACTIONS.EDIT, { featureName });
    const { activeOrganizationAccount } = useContext(UserContext);

    const [editingState, setEditingState] = useState<(AttachmentInput & { tagSet: string[] }) | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const securedActions = [
        {
            icon: Icons.Close,
            label: <I18n token="dialog.close" />,
            onClick: onClose
        }
    ];

    const panelTitle = editingState?.name || <I18n token="report.stepper.create" />;

    const buildInitialEditFile = (file: AttachmentInput): AttachmentInput & { tagSet: string[] } => {
        const input = createAttachmentInput(file);
        return {
            ...input,
            tagSet: input.tags || []
        };
    };

    const handleCloseEdit = () => {
        setEditingState(null);
    };

    const handleTagChange = (_name: string, value: string[], _index: number) => {
        if (!editingState) return;
        setEditingState((prev) => ({
            ...prev!,
            tagSet: value
        }));
    };

    const handleSaveEdit = () => {
        if (!editingState) return;
        const id = editingState.entityReference?.id as string;
        const tags = editingState.tagSet;
        setIsSaving(true);
        associateTag(activeOrganizationAccount, id, tags)
            .then(() => refetch())
            .catch(() => failedToastMessage('attachment.save.error'))
            .finally(() => {
                setIsSaving(false);
                handleCloseEdit();
            });
    };

    return (
        <SidePanel
            id={'status-reports-panel'}
            title={panelTitle}
            securedActions={securedActions}
            onBack={editingState ? handleCloseEdit : undefined}
        >
            {loading ? (
                <H sx={{ justifyContent: 'center', height: '100%' }}>
                    <Loading />
                </H>
            ) : (
                <ScrollableLayout>
                    {editingState ? (
                        <AttachmentsPreview
                            saveFiles={[editingState]}
                            onDelete={() => undefined} // tags-only edit; deletion is disabled intentionally
                            onTagChange={handleTagChange}
                            saveAttachment={handleSaveEdit}
                            isSaveDisabled={isSaving}
                            isSaving={isSaving}
                            onCancelEdit={handleCloseEdit}
                        />
                    ) : (
                        <AttachmentsView
                            attachments={attachments}
                            hasEntityCommentPermission
                            onEditAttachment={
                                canManage
                                    ? (file: AttachmentInput) => setEditingState(buildInitialEditFile(file))
                                    : undefined
                            }
                        />
                    )}
                </ScrollableLayout>
            )}
        </SidePanel>
    );
}
